/**
 * useAdminProducts Hook
 * State management for admin product operations
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { AdminProductsService } from '../services/AdminProductsService';
import { IAdminGeogridProduct, IAdminDrainageProduct, IAdminGeogridProductRequest, IAdminDrainageProductRequest, ProductType, IProductFilters } from '../types/AdminTypes';
import { ProductDataService } from '../../../services/ProductDataService';
import { DrainageDataService } from '../../../services/DrainageDataService';
import { GeogridLcrBridge } from '../../../services/GeogridLcrBridge';
import { materialDatabaseService } from '../../../services/MaterialDatabaseService';

interface UseAdminProductsReturn {
    // State
    geogridProducts: IAdminGeogridProduct[];
    drainageProducts: IAdminDrainageProduct[];
    filteredGeogridProducts: IAdminGeogridProduct[];
    filteredDrainageProducts: IAdminDrainageProduct[];
    loading: boolean;
    error: string | null;
    selectedProductType: ProductType;
    geogridFamilies: string[];
    drainageCategories: string[];
    filters: IProductFilters;

    // Actions
    setSelectedProductType: (type: ProductType) => void;
    setFilters: (filters: IProductFilters) => void;
    fetchGeogridProducts: () => Promise<void>;
    fetchDrainageProducts: () => Promise<void>;
    createGeogridProduct: (product: IAdminGeogridProductRequest) => Promise<IAdminGeogridProduct>;
    updateGeogridProduct: (id: string, product: IAdminGeogridProductRequest) => Promise<IAdminGeogridProduct>;
    deleteGeogridProduct: (id: string) => Promise<void>;
    createDrainageProduct: (product: IAdminDrainageProductRequest) => Promise<IAdminDrainageProduct>;
    updateDrainageProduct: (id: string, product: IAdminDrainageProductRequest) => Promise<IAdminDrainageProduct>;
    deleteDrainageProduct: (id: string) => Promise<void>;
    refreshProducts: () => Promise<void>;
    clearError: () => void;
}

export const useAdminProducts = (): UseAdminProductsReturn => {
    // State
    const [geogridProducts, setGeogridProducts] = useState<IAdminGeogridProduct[]>([]);
    const [drainageProducts, setDrainageProducts] = useState<IAdminDrainageProduct[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedProductType, setSelectedProductType] = useState<ProductType>('geogrid');
    const [geogridFamilies, setGeogridFamilies] = useState<string[]>([]);
    const [drainageCategories, setDrainageCategories] = useState<string[]>([]);
    const [filters, setFilters] = useState<IProductFilters>({});

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Fetch geogrid products
    const fetchGeogridProducts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [productsResponse, families] = await Promise.all([AdminProductsService.getGeogridProducts(), AdminProductsService.getGeogridFamilies()]);
            setGeogridProducts(productsResponse.products);
            setGeogridFamilies(families);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch geogrid products';
            setError(errorMessage);
            console.error('Error fetching geogrid products:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch drainage products
    const fetchDrainageProducts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [productsResponse, categories] = await Promise.all([AdminProductsService.getDrainageProducts(), AdminProductsService.getDrainageCategories()]);
            setDrainageProducts(productsResponse.products);
            setDrainageCategories(categories);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch drainage products';
            setError(errorMessage);
            console.error('Error fetching drainage products:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Refresh all products
    const refreshProducts = useCallback(async () => {
        if (selectedProductType === 'geogrid') {
            await fetchGeogridProducts();
        } else {
            await fetchDrainageProducts();
        }
    }, [selectedProductType, fetchGeogridProducts, fetchDrainageProducts]);

    // Create geogrid product
    const createGeogridProduct = useCallback(
        async (product: IAdminGeogridProductRequest): Promise<IAdminGeogridProduct> => {
            setLoading(true);
            setError(null);
            try {
                const newProduct = await AdminProductsService.createGeogridProduct(product);
                await fetchGeogridProducts(); // Refresh admin list
                try {
                    await ProductDataService.forceRefresh();
                    GeogridLcrBridge.invalidateCache();
                    materialDatabaseService.refreshGeogridProducts();
                } catch (e) {
                    console.error('Cache refresh failed:', e);
                }
                return newProduct;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to create geogrid product';
                setError(errorMessage);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [fetchGeogridProducts]
    );

    // Update geogrid product
    const updateGeogridProduct = useCallback(async (id: string, product: IAdminGeogridProductRequest): Promise<IAdminGeogridProduct> => {
        setLoading(true);
        setError(null);
        try {
            const updatedProduct = await AdminProductsService.updateGeogridProduct(id, product);
            // Update local state
            setGeogridProducts(prev => prev.map(p => (p.id === id ? updatedProduct : p)));
            try {
                await ProductDataService.forceRefresh();
                GeogridLcrBridge.invalidateCache();
                materialDatabaseService.refreshGeogridProducts();
            } catch (e) {
                console.error('Cache refresh failed:', e);
            }
            return updatedProduct;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update geogrid product';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Delete geogrid product
    const deleteGeogridProduct = useCallback(async (id: string): Promise<void> => {
        setLoading(true);
        setError(null);
        try {
            await AdminProductsService.deleteGeogridProduct(id);
            // Update local state
            setGeogridProducts(prev => prev.filter(p => p.id !== id));
            try {
                await ProductDataService.forceRefresh();
                GeogridLcrBridge.invalidateCache();
                materialDatabaseService.refreshGeogridProducts();
            } catch (e) {
                console.error('Cache refresh failed:', e);
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete geogrid product';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Create drainage product
    const createDrainageProduct = useCallback(
        async (product: IAdminDrainageProductRequest): Promise<IAdminDrainageProduct> => {
            setLoading(true);
            setError(null);
            try {
                const newProduct = await AdminProductsService.createDrainageProduct(product);
                await fetchDrainageProducts(); // Refresh admin list
                try {
                    await DrainageDataService.forceRefresh();
                } catch (e) {
                    console.error('Cache refresh failed:', e);
                }
                return newProduct;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to create drainage product';
                setError(errorMessage);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [fetchDrainageProducts]
    );

    // Update drainage product
    const updateDrainageProduct = useCallback(async (id: string, product: IAdminDrainageProductRequest): Promise<IAdminDrainageProduct> => {
        setLoading(true);
        setError(null);
        try {
            const updatedProduct = await AdminProductsService.updateDrainageProduct(id, product);
            // Update local state
            setDrainageProducts(prev => prev.map(p => (p.id === id ? updatedProduct : p)));
            try {
                await DrainageDataService.forceRefresh();
            } catch (e) {
                console.error('Cache refresh failed:', e);
            }
            return updatedProduct;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update drainage product';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Delete drainage product
    const deleteDrainageProduct = useCallback(async (id: string): Promise<void> => {
        setLoading(true);
        setError(null);
        try {
            await AdminProductsService.deleteDrainageProduct(id);
            // Update local state
            setDrainageProducts(prev => prev.filter(p => p.id !== id));
            try {
                await DrainageDataService.forceRefresh();
            } catch (e) {
                console.error('Cache refresh failed:', e);
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete drainage product';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Filter geogrid products
    const filteredGeogridProducts = useMemo(() => {
        let filtered = [...geogridProducts];

        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(p => p.prodotto.toLowerCase().includes(searchLower) || p.family.toLowerCase().includes(searchLower) || p.id.toLowerCase().includes(searchLower));
        }

        if (filters.family) {
            filtered = filtered.filter(p => p.family === filters.family);
        }

        if (filters.category) {
            filtered = filtered.filter(p => p.category === filters.category);
        }

        if (filters.projectType) {
            filtered = filtered.filter(p => p.applicableProjects?.includes(filters.projectType!) || false);
        }

        return filtered;
    }, [geogridProducts, filters]);

    // Filter drainage products
    const filteredDrainageProducts = useMemo(() => {
        let filtered = [...drainageProducts];

        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(p => p.displayName.toLowerCase().includes(searchLower) || p.category.toLowerCase().includes(searchLower) || p.id.toLowerCase().includes(searchLower));
        }

        if (filters.category) {
            filtered = filtered.filter(p => p.category === filters.category);
        }

        return filtered;
    }, [drainageProducts, filters]);

    // Initial fetch
    useEffect(() => {
        if (selectedProductType === 'geogrid') {
            fetchGeogridProducts();
        } else {
            fetchDrainageProducts();
        }
    }, [selectedProductType, fetchGeogridProducts, fetchDrainageProducts]);

    return {
        geogridProducts,
        drainageProducts,
        filteredGeogridProducts,
        filteredDrainageProducts,
        loading,
        error,
        selectedProductType,
        geogridFamilies,
        drainageCategories,
        filters,
        setSelectedProductType,
        setFilters,
        fetchGeogridProducts,
        fetchDrainageProducts,
        createGeogridProduct,
        updateGeogridProduct,
        deleteGeogridProduct,
        createDrainageProduct,
        updateDrainageProduct,
        deleteDrainageProduct,
        refreshProducts,
        clearError,
    };
};
